import { NavLink, useLocation } from 'react-router-dom';
import type { RefObject } from 'react';
import {
  BarChart3,
  BookOpen,
  Bot,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  DollarSign,
  FileText,
  FolderOpen,
  HelpCircle,
  Kanban,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MoreHorizontal,
  Settings,
  StickyNote,
  UserCog,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getBulletinEnabled } from '../api/bulletin';
import { ROLE_RANK, getRoleRank } from '../constants/roles';

const SIDEBAR_EXPANDED = 200;
const SIDEBAR_COLLAPSED = 56;

interface SidebarNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: boolean;
  featureFlag?: string;
  hideOnMobile?: boolean;
  minRole?: string;
  roles?: string[];
}

const navItems: SidebarNavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, badge: true },
  { to: '/pipeline', label: 'Pipeline', icon: Kanban, hideOnMobile: true },
  { to: '/tasks', label: 'Tasks', icon: ListChecks },
  { to: '/ai-hub', label: 'AI Tasks', icon: Bot },
  { to: '/law-library', label: 'Law Library', icon: BookOpen, minRole: 'paralegal' },
  { to: '/matters', label: 'Matters', icon: FolderOpen },
  { to: '/matters/needs-review', label: 'Needs Review', icon: ClipboardList, minRole: 'intake' },
  { to: '/contacts', label: 'Contacts', icon: Users },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/documents', label: 'Documents', icon: FileText },
  { to: '/staff-portal', label: 'Staff Portal', icon: BookOpen, hideOnMobile: true },
  { to: '/bulletin-board', label: 'Bulletin Board', icon: StickyNote, hideOnMobile: true, featureFlag: 'bulletin_board' },
];

const moreMenuItems: SidebarNavItem[] = [
  { to: '/settings', label: 'My Account', icon: UserCog },
  { to: '/guide', label: 'Guide', icon: HelpCircle },
  { to: '/employee-manual', label: 'Employee Manual', icon: BookOpen },
];

interface SidebarSharedProps {
  authEnabled: boolean;
  canViewKpis: boolean;
  currentUserRole?: string | null;
  firm: {
    sabrinaEnabled?: boolean;
    sabrinaUrl?: string;
  };
  isAdmin: boolean;
  portalQueueCount: number;
  user: unknown;
  logout: () => void;
}

interface AppSidebarNavProps extends SidebarSharedProps {
  collapsed: boolean;
  isDrawer: boolean;
  onCloseDrawer: () => void;
  showMoreMenu?: boolean;
  setShowMoreMenu?: (show: boolean) => void;
  moreMenuRef?: RefObject<HTMLDivElement | null>;
}

interface AppDesktopSidebarProps extends SidebarSharedProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  showMoreMenu: boolean;
  setShowMoreMenu: (show: boolean) => void;
  moreMenuRef: RefObject<HTMLDivElement | null>;
}

interface AppMobileDrawerProps extends SidebarSharedProps {
  drawerOpen: boolean;
  onCloseDrawer: () => void;
}

const navLinkStyle = (collapsed: boolean, isActive: boolean, isDrawer: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: (!isDrawer && collapsed) ? 0 : '0.6rem',
  padding: (!isDrawer && collapsed) ? '0.75rem 0' : '0.75rem 1rem',
  justifyContent: (!isDrawer && collapsed) ? 'center' as const : 'flex-start' as const,
  color: isActive ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
  background: isActive ? 'var(--sidebar-active)' : 'transparent',
  textDecoration: 'none',
  fontSize: '0.9rem',
  fontWeight: isActive ? 600 : 400,
  borderLeft: isActive ? '3px solid #fb7185' : '3px solid transparent',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden' as const,
  transition: 'all 0.15s ease',
});

function BrandMark() {
  return (
    <div>
      <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '-0.02em' }}>Valentine</h2>
      <div style={{
        width: 32,
        height: 3,
        background: 'linear-gradient(90deg, #f43f5e, #f59e0b)',
        borderRadius: 2,
        marginTop: 4,
      }} />
      <span style={{ fontSize: '0.65rem', color: 'var(--sidebar-text)', fontWeight: 500 }}>v2.0</span>
    </div>
  );
}

function AppSidebarNav({
  authEnabled,
  canViewKpis,
  collapsed,
  currentUserRole,
  firm,
  isAdmin,
  isDrawer,
  logout,
  moreMenuRef,
  onCloseDrawer,
  portalQueueCount,
  setShowMoreMenu = () => undefined,
  showMoreMenu = false,
  user,
}: AppSidebarNavProps) {
  const location = useLocation();
  const userRank = getRoleRank(currentUserRole ?? 'admin');
  const { data: bulletinFlag } = useQuery({
    queryKey: ['bulletin-enabled'],
    queryFn: getBulletinEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const linkStyle = (isActive: boolean) => navLinkStyle(collapsed, isActive, isDrawer);
  const closeDrawerIfNeeded = isDrawer ? onCloseDrawer : undefined;

  return (
    <>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0', flex: 1 }}>
        {navItems.filter((item) => {
          if (item.minRole && userRank < (ROLE_RANK[item.minRole] ?? 1)) return false;
          if (item.roles && !item.roles.includes(currentUserRole ?? '')) return false;
          if (isDrawer && item.hideOnMobile) return false;
          if (item.featureFlag === 'bulletin_board' && bulletinFlag?.enabled === false) return false;
          return true;
        }).map((item) => {
          const Icon = item.icon;
          const badgeCount = item.badge ? portalQueueCount : 0;
          const linkTo = badgeCount > 0 && item.to === '/' ? '/#portal-queue' : item.to;

          return (
            <li key={item.to}>
              <NavLink
                to={linkTo}
                end={item.to === '/' && badgeCount === 0}
                onClick={closeDrawerIfNeeded}
                style={({ isActive }) => linkStyle(isActive)}
                title={(!isDrawer && collapsed) ? item.label : undefined}
                onMouseEnter={(e) => {
                  const target = e.currentTarget;
                  if (!target.classList.contains('active')) {
                    target.style.background = 'rgba(255,255,255,0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget;
                  if (!target.classList.contains('active')) {
                    target.style.background = '';
                  }
                }}
              >
                <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                  <Icon size={18} />
                  {badgeCount > 0 && (!isDrawer && collapsed) && (
                    <span style={{
                      position: 'absolute',
                      top: -4,
                      right: -6,
                      background: '#f43f5e',
                      color: '#fff',
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      borderRadius: 6,
                      minWidth: 14,
                      height: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 3px',
                    }}>
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </span>
                {(isDrawer || !collapsed) && (
                  <>
                    {item.label}
                    {badgeCount > 0 && (
                      <span
                        title={`${badgeCount} document${badgeCount !== 1 ? 's' : ''} awaiting portal approval`}
                        style={{
                          marginLeft: 'auto',
                          background: '#f43f5e',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          borderRadius: 8,
                          minWidth: 18,
                          height: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 4px',
                        }}
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          );
        })}

        {isAdmin && (
          <>
            <li style={{ padding: (!isDrawer && collapsed) ? '0.5rem 0' : '0.5rem 1rem 0.25rem', borderTop: '1px solid var(--sidebar-border)', marginTop: '0.5rem' }}>
              {(isDrawer || !collapsed) && <span style={{ fontSize: '0.6rem', color: 'var(--sidebar-text)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em' }}>Admin</span>}
            </li>
            <li>
              <NavLink
                to="/admin"
                onClick={closeDrawerIfNeeded}
                style={() => linkStyle(location.pathname.startsWith('/admin'))}
                title={(!isDrawer && collapsed) ? 'Admin' : undefined}
              >
                <Settings size={18} style={{ flexShrink: 0 }} />
                {(isDrawer || !collapsed) && 'Admin'}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/reports"
                onClick={closeDrawerIfNeeded}
                style={({ isActive }) => linkStyle(isActive)}
                title={(!isDrawer && collapsed) ? 'Reports' : undefined}
              >
                <BarChart3 size={18} style={{ flexShrink: 0 }} />
                {(isDrawer || !collapsed) && 'Reports'}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/kpis"
                onClick={closeDrawerIfNeeded}
                style={({ isActive }) => linkStyle(isActive)}
                title={(!isDrawer && collapsed) ? 'KPIs' : undefined}
              >
                <BarChart3 size={18} style={{ flexShrink: 0 }} />
                {(isDrawer || !collapsed) && 'KPIs'}
              </NavLink>
            </li>
            {firm.sabrinaEnabled && (
              <li>
                <a
                  href={`${firm.sabrinaUrl}/timecards`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeDrawerIfNeeded}
                  style={linkStyle(false)}
                  title={(!isDrawer && collapsed) ? 'Payroll & Timecards' : undefined}
                >
                  <DollarSign size={18} style={{ flexShrink: 0 }} />
                  {(isDrawer || !collapsed) && 'Payroll & Timecards'}
                </a>
              </li>
            )}
          </>
        )}

        {canViewKpis && !isAdmin && (
          <>
            <li style={{ padding: (!isDrawer && collapsed) ? '0.5rem 0' : '0.5rem 1rem 0.25rem', borderTop: '1px solid var(--sidebar-border)', marginTop: '0.5rem' }}>
              {(isDrawer || !collapsed) && <span style={{ fontSize: '0.6rem', color: 'var(--sidebar-text)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em' }}>Supervisor</span>}
            </li>
            <li>
              <NavLink
                to="/admin/kpis"
                onClick={closeDrawerIfNeeded}
                style={({ isActive }) => linkStyle(isActive)}
                title={(!isDrawer && collapsed) ? 'KPIs' : undefined}
              >
                <BarChart3 size={18} style={{ flexShrink: 0 }} />
                {(isDrawer || !collapsed) && 'KPIs'}
              </NavLink>
            </li>
          </>
        )}

        {isDrawer && (
          <>
            <li style={{ padding: '0.5rem 1rem 0.25rem', borderTop: '1px solid var(--sidebar-border)', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.6rem', color: 'var(--sidebar-text)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em' }}>More</span>
            </li>
            {moreMenuItems.filter((item) => !item.hideOnMobile).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onCloseDrawer}
                    style={() => navLinkStyle(false, isActive, true)}
                  >
                    <Icon size={18} style={{ flexShrink: 0 }} />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </>
        )}
      </ul>

      {!isDrawer && (
        <div ref={moreMenuRef} style={{ position: 'relative', borderTop: '1px solid var(--sidebar-border)', padding: '0.5rem 0' }}>
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : '0.6rem',
              padding: collapsed ? '0.6rem 0' : '0.6rem 0.85rem',
              justifyContent: collapsed ? 'center' : 'flex-start',
              width: collapsed ? '100%' : 'calc(100% - 0.6rem)',
              margin: collapsed ? 0 : '0 0.3rem',
              background: showMoreMenu ? 'rgba(251, 113, 133, 0.2)' : 'rgba(251, 113, 133, 0.08)',
              border: '1px solid rgba(251, 113, 133, 0.15)',
              borderRadius: collapsed ? 0 : '8px',
              color: showMoreMenu ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
              fontSize: '0.9rem',
              fontWeight: showMoreMenu ? 600 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              transition: 'all 0.15s ease',
            }}
            title={collapsed ? 'More' : undefined}
            onMouseEnter={(e) => { if (!showMoreMenu) e.currentTarget.style.background = 'rgba(251, 113, 133, 0.2)'; }}
            onMouseLeave={(e) => { if (!showMoreMenu) e.currentTarget.style.background = 'rgba(251, 113, 133, 0.08)'; }}
          >
            <MoreHorizontal size={18} style={{ flexShrink: 0 }} />
            {!collapsed && 'More'}
          </button>

          {showMoreMenu && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: collapsed ? '100%' : '0.5rem',
              marginBottom: collapsed ? 0 : '0.25rem',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
              padding: '0.35rem 0',
              minWidth: 180,
              zIndex: 100,
            }}>
              {moreMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setShowMoreMenu(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.85rem',
                      color: isActive ? 'var(--primary)' : 'var(--text)',
                      background: isActive ? 'var(--primary-light)' : 'transparent',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 400,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = ''; }}
                  >
                    <Icon size={16} />
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isDrawer && authEnabled && user && (
        <div style={{ borderTop: '1px solid var(--sidebar-border)', padding: '0.5rem 0' }}>
          <button
            onClick={() => { onCloseDrawer(); logout(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              padding: '0.6rem 1rem',
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: 'var(--sidebar-text)',
              fontSize: '0.9rem',
              cursor: 'pointer',
              borderLeft: '3px solid transparent',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
          >
            <LogOut size={18} style={{ flexShrink: 0 }} />
            Sign Out
          </button>
        </div>
      )}
    </>
  );
}

export function AppDesktopSidebar({
  collapsed,
  setCollapsed,
  ...navProps
}: AppDesktopSidebarProps) {
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <nav
      style={{
        width: sidebarWidth,
        background: 'linear-gradient(180deg, #1a1023, #2d1f3d)',
        color: '#fff',
        padding: '1rem 0',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(26, 16, 35, 0.3)',
      }}
    >
      <div style={{
        padding: collapsed ? '0 0.5rem 1.5rem' : '0 1rem 1.5rem',
        borderBottom: '1px solid var(--sidebar-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 32,
      }}>
        {!collapsed && <BrandMark />}
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--sidebar-text)',
            cursor: 'pointer',
            padding: '0.2rem',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
          }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <AppSidebarNav
        {...navProps}
        collapsed={collapsed}
        isDrawer={false}
        onCloseDrawer={() => undefined}
      />
    </nav>
  );
}

export function AppMobileDrawer({
  drawerOpen,
  onCloseDrawer,
  ...navProps
}: AppMobileDrawerProps) {
  return (
    <>
      {drawerOpen && (
        <div
          onClick={onCloseDrawer}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--overlay)',
            zIndex: 999,
            transition: 'opacity 0.25s ease',
          }}
        />
      )}
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 280,
          height: '100dvh',
          background: 'linear-gradient(180deg, #1a1023, #2d1f3d)',
          color: '#fff',
          padding: '1rem 0',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '2px 0 12px rgba(26, 16, 35, 0.3)',
          zIndex: 1000,
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
          overflowY: 'auto',
        }}
      >
        <div style={{
          padding: '0 1rem 1.5rem',
          borderBottom: '1px solid var(--sidebar-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 32,
        }}>
          <BrandMark />
          <button
            onClick={onCloseDrawer}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--sidebar-text)',
              cursor: 'pointer',
              padding: '0.2rem',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
            title="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <AppSidebarNav
          {...navProps}
          collapsed={false}
          isDrawer
          onCloseDrawer={onCloseDrawer}
        />
      </nav>
    </>
  );
}
